import { createFileRoute, Navigate, Outlet, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import type { AppAbility } from '@shared/types/ability';
import { canAccessAnalyticsMove } from '@shared/lib';
import { useAbility } from '@features/authorization';
import { useAuth } from '@features/auth';
import { Header } from '@widgets/header';

type AnalyticsMoveRouteContext = {
  auth: { isLoading: boolean; isAuthenticated: boolean };
  ability: AppAbility;
};

export const Route = createFileRoute('/$locale/_authenticated/analytics-move')({
  beforeLoad: ({ context, params }) => {
    const { auth, ability } = context as unknown as AnalyticsMoveRouteContext;

    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessAnalyticsMove(ability)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({
        to: '/$locale/unauthorized',
        params: { locale: params.locale },
      });
    }
  },
  component: AnalyticsMoveLayout,
});

function AnalyticsMoveLayout() {
  const { locale: rawLocale } = Route.useParams();
  const locale = rawLocale as unknown as string;
  const navigate = useNavigate();

  const ability = useAbility();
  const auth = useAuth();

  useEffect(() => {
    if (auth.isLoading || !auth.isAuthenticated) {
      return;
    }

    if (!canAccessAnalyticsMove(ability)) {
      void navigate({
        to: '/$locale/unauthorized',
        params: { locale },
        replace: true,
      });
    }
  }, [ability, auth.isAuthenticated, auth.isLoading, locale, navigate]);

  if (!auth.isLoading && auth.isAuthenticated && !canAccessAnalyticsMove(ability)) {
    return <Navigate to="/$locale/unauthorized" params={{ locale }} replace />;
  }

  return (
    <div className="flex min-h-screen flex-col pt-16 min-[992px]:pt-0">
      <Header locale={locale} className="min-[992px]:hidden" />

      <main className="min-h-0 min-w-0 flex-1 overflow-y-auto p-6 [scrollbar-gutter:stable_both-edges]">
        <Outlet />
      </main>
    </div>
  );
}
